import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Truck, Lightbulb } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Define the feature interface
interface Feature {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  expandedContent: string;
  additionalContent: string;
}

// Define props interface for FeatureCard
interface FeatureCardProps {
  feature: Feature;
  fullWidth?: boolean;
  language: string;
}

// Define props interface for FeatureSections
interface FeatureSectionsProps {
  language?: string;
}

// Define translations for FeatureSections content
const TRANSLATIONS: Record<string, {
  features: Feature[];
  buttonContactUs: string;
  buttonShowMore: string;
  buttonShowLess: string;
}> = {
  en: {
    features: [
      {
        icon: ShieldCheck,
        title: "PRECISION ENGINEERING",
        description: "Unrivaled craftsmanship defining global quality standards.",
        expandedContent: "Our Precision Engineering showcases the pinnacle of 'Made in Germany' excellence. Each product is crafted with meticulous attention to detail, leveraging advanced manufacturing techniques and rigorous quality controls. We set the benchmark for durability, performance, and reliability trusted worldwide.",
        additionalContent: "Our state-of-the-art facilities utilize cutting-edge CNC machining, robotic assembly, and real-time quality monitoring. Certified to ISO 9001 and DIN standards, we offer bespoke engineering solutions tailored to industries like automotive, aerospace, and industrial manufacturing, ensuring unparalleled precision."
      },
      {
        icon: Truck,
        title: "GLOBAL DISTRIBUTION NETWORK",
        description: "Seamless delivery to every corner of the world.",
        expandedContent: "Our Global Distribution Network ensures 'Made in Germany' products reach you with unmatched efficiency. With strategically located hubs and optimized logistics, we deliver precision-engineered goods to over 100 countries, maintaining speed, reliability, and sustainability in every shipment.",
        additionalContent: "Leveraging AI-driven supply chain optimization and eco-friendly transport solutions, we operate distribution centers in Europe, Asia, and the Americas. Our partnerships with leading logistics providers guarantee timely deliveries, while our carbon-neutral shipping options align with global sustainability goals."
      },
      {
        icon: Lightbulb,
        title: "INNOVATIVE PRODUCT DESIGN",
        description: "Pioneering designs powered by cutting-edge technology.",
        expandedContent: "Our Innovative Product Design redefines industry standards with 'Made in Germany' ingenuity. We integrate AI, IoT, and smart materials to create futuristic products that blend aesthetics with functionality, driving technological advancement across global markets.",
        additionalContent: "Our R&D team collaborates with top engineers to develop smart tools, energy-efficient devices, and customizable designs using 3D printing and simulation software. We hold multiple patents for groundbreaking innovations, positioning us as leaders in tech-driven product development."
      }
    ],
    buttonContactUs: "Contact Us",
    buttonShowMore: "Show More",
    buttonShowLess: "Show Less"
  },
  de: {
    features: [
      {
        icon: ShieldCheck,
        title: "PRÄZISIONSMASCHINENBAU",
        description: "Unübertroffene Handwerkskunst, die globale Qualitätsstandards setzt.",
        expandedContent: "Unser Präzisionsmaschinenbau repräsentiert die Spitze der 'Made in Germany' Exzellenz. Jedes Produkt wird mit akribischer Sorgfalt gefertigt, unter Verwendung fortschrittlicher Fertigungstechniken und strenger Qualitätskontrollen. Wir setzen den Maßstab für Langlebigkeit, Leistung und Zuverlässigkeit, weltweit geschätzt.",
        additionalContent: "Unsere hochmodernen Anlagen nutzen CNC-Maschinenbau, robotergestützte Montage und Echtzeit-Qualitätsüberwachung. Zertifiziert nach ISO 9001 und DIN-Standards, bieten wir maßgeschneiderte Ingenieurlösungen für Branchen wie Automobil, Luftfahrt und Industriefertigung mit unvergleichlicher Präzision."
      },
      {
        icon: Truck,
        title: "GLOBALES VERTEILUNGSNETZ",
        description: "Nahtlose Lieferung in jede Ecke der Welt.",
        expandedContent: "Unser globales Verteilungsnetz stellt sicher, dass 'Made in Germany' Produkte mit einzigartiger Effizienz zu Ihnen gelangen. Mit strategisch platzierten Standorten und optimierter Logistik liefern wir präzisionsgefertigte Waren in über 100 Länder, unter Wahrung von Geschwindigkeit, Zuverlässigkeit und Nachhaltigkeit bei jeder Sendung.",
        additionalContent: "Dank KI-gestützter Lieferkettenoptimierung und umweltfreundlicher Transportlösungen betreiben wir Verteilungsstellen in Europa, Asien und Amerika. Unsere Partnerschaften mit führenden Logistikdienstleistern gewährleisten pünktliche Lieferungen, während unsere klimaneutralen Versandoptionen mit globalen Nachhaltigkeitszielen übereinstimmen."
      },
      {
        icon: Lightbulb,
        title: "INNOVATIVES PRODUKTENTWURF",
        description: "Pionierhafte Designs durch Spitzen-Technologie.",
        expandedContent: "Unser innovatives Produktentwurf setzt mit 'Made in Germany' Einfallsreichtum neue Maßstäbe. Wir integrieren KI, IoT und smarte Materialien, um futuristische Produkte zu schaffen, die Ästhetik mit Funktionalität verbinden und technologischen Fortschritt in globalen Märkten vorantreiben.",
        additionalContent: "Unser Forschungs- und Entwicklungsteam arbeitet mit Top-Ingenieuren zusammen, um smarte Werkzeuge, energieeffiziente Geräte und anpassbare Designs mit 3D-Druck und Simulationssoftware zu entwickeln. Wir halten mehrere Patente für bahnbrechende Innovationen, was uns zu Führern in der technologiegetriebenen Produktentwicklung macht."
      }
    ],
    buttonContactUs: "Kontaktieren Sie Uns",
    buttonShowMore: "Mehr Anzeigen",
    buttonShowLess: "Weniger Anzeigen"
  },
  es: {
    features: [
      {
        icon: ShieldCheck,
        title: "INGENIERÍA DE PRECISIÓN",
        description: "Artesanía insuperable que define estándares de calidad globales.",
        expandedContent: "Nuestra Ingeniería de Precisión representa la cima de la excelencia 'Made in Germany'. Cada producto se elabora con una atención meticulosa al detalle, utilizando técnicas de fabricación avanzadas y estrictos controles de calidad. Establecemos el estándar para durabilidad, rendimiento y confiabilidad, reconocidos mundialmente.",
        additionalContent: "Nuestras instalaciones de última generación emplean mecanizado CNC, ensamblaje robótico y monitoreo de calidad en tiempo real. Certificados según ISO 9001 y normas DIN, ofrecemos soluciones de ingeniería personalizadas para industrias como automotriz, aeroespacial y manufactura industrial, garantizando una precisión sin igual."
      },
      {
        icon: Truck,
        title: "RED DE DISTRIBUCIÓN GLOBAL",
        description: "Entrega sin inconvenientes a cada rincón del mundo.",
        expandedContent: "Nuestra Red de Distribución Global asegura que los productos 'Made in Germany' lleguen a usted con una eficiencia inigualable. Con centros estratégicamente ubicados y logística optimizada, distribuimos bienes de ingeniería de precisión a más de 100 países, manteniendo velocidad, confiabilidad y sostenibilidad en cada envío.",
        additionalContent: "Utilizando optimización de cadenas de suministro impulsada por IA y soluciones de transporte ecológicas, operamos centros de distribución en Europa, Asia y América. Nuestras alianzas con proveedores logísticos líderes garantizan entregas puntuales, mientras que nuestras opciones de envío neutro en carbono se alinean con objetivos de sostenibilidad global."
      },
      {
        icon: Lightbulb,
        title: "DISEÑO INNOVADOR DE PRODUCTOS",
        description: "Diseños pioneros impulsados por tecnología de punta.",
        expandedContent: "Nuestro Diseño Innovador de Productos redefine los estándares industriales con la ingeniosidad 'Made in Germany'. Integramos IA, IoT y materiales inteligentes para crear productos futuristas que combinan estética con funcionalidad, impulsando el avance tecnológico en mercados globales.",
        additionalContent: "Nuestro equipo de I+D colabora con ingenieros de élite para desarrollar herramientas inteligentes, dispositivos eficientes en energía y diseños personalizables usando impresión 3D y software de simulación. Poseemos múltiples patentes por innovaciones revolucionarias, posicionándonos como líderes en desarrollo de productos tecnológicos."
      }
    ],
    buttonContactUs: "Contáctenos",
    buttonShowMore: "Mostrar Más",
    buttonShowLess: "Mostrar Menos"
  },
  fr: {
    features: [
      {
        icon: ShieldCheck,
        title: "INGÉNIERIE DE PRÉCISION",
        description: "Un savoir-faire inégalé qui définit les normes de qualité mondiales.",
        expandedContent: "Notre Ingénierie de Précision incarne l’excellence 'Made in Germany'. Chaque produit est façonné avec un soin méticuleux, en utilisant des techniques de fabrication avancées et des contrôles de qualité rigoureux. Nous établissons la référence en matière de durabilité, de performance et de fiabilité, reconnues à l’échelle mondiale.",
        additionalContent: "Nos installations de pointe utilisent des machines CNC, des assemblages robotisés et un suivi de qualité en temps réel. Certifiés ISO 9001 et aux normes DIN, nous proposons des solutions d’ingénierie sur mesure pour des secteurs comme l’automobile, l’aérospatiale et la fabrication industrielle, garantissant une précision inégalée."
      },
      {
        icon: Truck,
        title: "RÉSEAU DE DISTRIBUTION MONDIAL",
        description: "Livraison fluide dans chaque recoin du monde.",
        expandedContent: "Notre Réseau de Distribution Mondial garantit que les produits 'Made in Germany' vous parviennent avec une efficacité inégalée. Grâce à des hubs stratégiquement placés et une logistique optimisée, nous livrons des biens d’ingénierie de précision à plus de 100 pays, alliant vitesse, fiabilité et durabilité à chaque envoi.",
        additionalContent: "En s’appuyant sur une optimisation de la chaîne d’approvisionnement par IA et des solutions de transport écologiques, nous exploitons des centres de distribution en Europe, en Asie et en Amérique. Nos partenariats avec des leaders logistiques assurent des livraisons ponctuelles, tandis que nos options d’expédition neutres en carbone s’alignent sur les objectifs de durabilité mondiaux."
      },
      {
        icon: Lightbulb,
        title: "CONCEPTION INNOVANTE DE PRODUITS",
        description: "Des designs pionniers alimentés par des technologies de pointe.",
        expandedContent: "Notre Conception Innovante de Produits redéfinit les normes industrielles avec l’ingéniosité 'Made in Germany'. Nous intégrons l’IA, l’IoT et des matériaux intelligents pour créer des produits futuristes mêlant esthétique et fonctionnalité, propulsant l’avancement technologique sur les marchés mondiaux.",
        additionalContent: "Notre équipe R&D collabore avec des ingénieurs d’élite pour développer des outils intelligents, des appareils écoénergétiques et des designs personnalisables grâce à l’impression 3D et aux logiciels de simulation. Nous détenons plusieurs brevets pour des innovations révolutionnaires, nous positionnant en leaders du développement de produits technologiques."
      }
    ],
    buttonContactUs: "Contactez-Nous",
    buttonShowMore: "En Voir Plus",
    buttonShowLess: "En Voir Moins"
  },
  it: {
    features: [
      {
        icon: ShieldCheck,
        title: "INGEGNERIA DI PRECISIONE",
        description: "Artigianato insuperabile che definisce gli standard di qualità globali.",
        expandedContent: "La nostra Ingegneria di Precisione incarna l’eccellenza 'Made in Germany'. Ogni prodotto è realizzato con attenzione maniacale ai dettagli, utilizzando tecniche di produzione avanzate e rigorosi controlli di qualità. Stabiliamo lo standard per durata, prestazioni e affidabilità, riconosciuti in tutto il mondo.",
        additionalContent: "Le nostre strutture all’avanguardia impiegano lavorazioni CNC, assemblaggi robotici e monitoraggio della qualità in tempo reale. Certificati ISO 9001 e secondo le norme DIN, offriamo soluzioni di ingegneria su misura per settori come automobilistico, aerospaziale e manifattura industriale, garantendo una precisione senza pari."
      },
      {
        icon: Truck,
        title: "RETE DI DISTRIBUZIONE GLOBALE",
        description: "Consegna senza interruzioni in ogni angolo del mondo.",
        expandedContent: "La nostra Rete di Distribuzione Globale assicura che i prodotti 'Made in Germany' ti raggiungano con un’efficienza incomparabile. Con hub strategicamente posizionati e logistica ottimizzata, distribuiamo beni di ingegneria di precisione in oltre 100 paesi, mantenendo velocità, affidabilità e sostenibilità in ogni spedizione.",
        additionalContent: "Utilizzando l’ottimizzazione della supply chain guidata da IA e soluzioni di trasporto ecologiche, operiamo centri di distribuzione in Europa, Asia e Americhe. Le nostre partnership con i principali provider logistici garantiscono consegne puntuali, mentre le opzioni di spedizione a zero emissioni si allineano agli obiettivi di sostenibilità globale."
      },
      {
        icon: Lightbulb,
        title: "DESIGN INNOVATIVO DI PRODOTTI",
        description: "Design pionieristici alimentati da tecnologie all’avanguardia.",
        expandedContent: "Il nostro Design Innovativo di Prodotti ridefinisce gli standard industriali con l’ingegno 'Made in Germany'. Integra IA, IoT e materiali intelligenti per creare prodotti futuristici che uniscono estetica e funzionalità, guidando l’avanzamento tecnologico nei mercati globali.",
        additionalContent: "Il nostro team R&S collabora con ingegneri di élite per sviluppare strumenti intelligenti, dispositivi ad alta efficienza energetica e design personalizzabili con stampa 3D e software di simulazione. Possediamo numerosi brevetti per innovazioni rivoluzionarie, posizionandoci come leader nello sviluppo di prodotti tecnologici."
      }
    ],
    buttonContactUs: "Contattaci",
    buttonShowMore: "Mostra di Più",
    buttonShowLess: "Mostra di Meno"
  },
  nl: {
    features: [
      {
        icon: ShieldCheck,
        title: "PRECISIE-ENGINEERING",
        description: "Onovertroffen vakmanschap dat wereldwijde kwaliteitsnormen bepaalt.",
        expandedContent: "Onze Precisie-Engineering belichaamt de top van 'Made in Germany' excellentie. Elk product wordt met uiterste zorg vervaardigd, met geavanceerde productietechnieken en strenge kwaliteitscontroles. We stellen de standaard voor duurzaamheid, prestaties en betrouwbaarheid, wereldwijd gewaardeerd.",
        additionalContent: "Onze ultramoderne faciliteiten maken gebruik van CNC-bewerking, robotgestuurde assemblage en realtime kwaliteitsbewaking. Gecertificeerd volgens ISO 9001 en DIN-normen, bieden we op maat gemaakte ingenieursoplossingen voor sectoren zoals automotive, luchtvaart en industriële fabricage, met ongeëvenaarde precisie."
      },
      {
        icon: Truck,
        title: "WERELDWIJD DISTRIBUTIENETWERK",
        description: "Naadloze levering naar elke uithoek van de wereld.",
        expandedContent: "Ons Wereldwijd Distributienetwerk zorgt ervoor dat 'Made in Germany' producten u bereiken met ongeëvenaarde efficiëntie. Met strategisch geplaatste hubs en geoptimaliseerde logistiek leveren we precisieproducten aan meer dan 100 landen, met behoud van snelheid, betrouwbaarheid en duurzaamheid bij elke verzending.",
        additionalContent: "Met AI-gestuurde supply chain-optimalisatie en milieuvriendelijke transportoplossingen beheren we distributiecentra in Europa, Azië en Amerika. Onze samenwerkingen met toonaangevende logistieke aanbieders garanderen tijdige leveringen, terwijl onze koolstofneutrale verzendopties aansluiten bij mondiale duurzaamheidsdoelen."
      },
      {
        icon: Lightbulb,
        title: "INNOVEREND PRODUCTONTWERP",
        description: "Pioniersontwerpen aangedreven door geavanceerde technologie.",
        expandedContent: "Ons Innovatief Productontwerp herdefinieert industriestandaarden met 'Made in Germany' vernuft. We integreren AI, IoT en slimme materialen om futuristische producten te creëren die esthetiek combineren met functionaliteit, en technologische vooruitgang stimuleren op wereldmarkten.",
        additionalContent: "Ons R&D-team werkt samen met topingenieurs om slimme tools, energiezuinige apparaten en aanpasbare ontwerpen te ontwikkelen met 3D-printen en simulatiesoftware. We bezitten meerdere patenten voor baanbrekende innovaties, wat ons positioneert als leiders in technologiegedreven productontwikkeling."
      }
    ],
    buttonContactUs: "Neem Contact Op",
    buttonShowMore: "Toon Meer",
    buttonShowLess: "Toon Minder"
  },
  sa: {
    features: [
      {
        icon: ShieldCheck,
        title: "الهندسة الدقيقة",
        description: "حرفية متميزة تحدد معايير الجودة العالمية.",
        expandedContent: "هندستنا الدقيقة تعكس ذروة التفوق 'صنع في ألمانيا'. يتم تصنيع كل منتج بعناية فائقة للتفاصيل، باستخدام تقنيات تصنيع متطورة ومراقبة جودة صارمة. نحن نضع معيار المتانة والأداء والموثوقية المعتمد عالميًا.",
        additionalContent: "تستخدم منشآتنا الحديثة أحدث تقنيات التصنيع CNC والتجميع الروبوتي ومراقبة الجودة في الوقت الفعلي. معتمدة وفقًا لمعايير ISO 9001 وDIN، نقدم حلول هندسية مخصصة لصناعات مثل السيارات والفضاء والتصنيع الصناعي، مضمونة بدقة استثنائية."
      },
      {
        icon: Truck,
        title: "شبكة التوزيع العالمية",
        description: "توصيل سلس إلى كل زاوية في العالم.",
        expandedContent: "شبكتنا العالمية للتوزيع تضمن وصول منتجات 'صنع في ألمانيا' إليك بكفاءة لا مثيل لها. مع مراكز استراتيجية ولوجستيات محسّنة، نوفر بضائع هندسية دقيقة إلى أكثر من 100 دولة، مع الحفاظ على السرعة والموثوقية والاستدامة في كل شحنة.",
        additionalContent: "بفضل تحسين سلاسل التوريد المدعومة بالذكاء الاصطناعي وحلول النقل الصديقة للبيئة، ندير مراكز توزيع في أوروبا وآسيا والأمريكتين. شراكاتنا مع مزودي الخدمات اللوجستية الرائدين تضمن التسليم في الوقت المحدد، بينما تتماشى خيارات الشحن الحيادي للكربون مع أهداف الاستدامة العالمية."
      },
      {
        icon: Lightbulb,
        title: "تصميم المنتجات الابتكاري",
        description: "تصاميم رائدة مدعومة بتقنيات متطورة.",
        expandedContent: "تصميم منتجاتنا الابتكاري يعيد تعريف المعايير الصناعية ببراعة 'صنع في ألمانيا'. ندمج الذكاء الاصطناعي وإنترنت الأشياء والمواد الذكية لخلق منتجات مستقبلية تجمع بين الجماليات والوظائف، مدعية التقدم التكنولوجي في الأسواق العالمية.",
        additionalContent: "فريق البحث والتطوير لدينا يتعاون مع مهندسين متميزين لتطوير أدوات ذكية وأجهزة فعالة في استهلاك الطاقة وتصاميم قابلة للتخصيص باستخدام الطباعة ثلاثية الأبعاد وبرمجيات المحاكاة. نحن نمتلك براءات اختراع متعددة لابتكارات ثورية، مما يضعنا كقادة في تطوير المنتجات المدعومة تكنولوجيًا."
      }
    ],
    buttonContactUs: "اتصل بنا",
    buttonShowMore: "عرض المزيد",
    buttonShowLess: "عرض أقل"
  },
  hk: {
    features: [
      {
        icon: ShieldCheck,
        title: "精密工程",
        description: "無與倫比嘅工藝，定義全球品質標準。",
        expandedContent: "我哋嘅精密工程展現'德國製造'嘅極致卓越。每一件產品以極致嘅細節關注打造，運用先進製造技術同嚴格嘅品質控制。我們訂立耐用性、性能同可靠性嘅全球標準，備受世界信賴。",
        additionalContent: "我哋嘅尖端設施採用CNC加工、機械人組裝同即時品質監控。通過ISO 9001同DIN認證，我哋為汽車、航空同工業製造等行業提供定制工程解決方案，確保無與倫比嘅精度。"
      },
      {
        icon: Truck,
        title: "全球分銷網絡",
        description: "無縫送貨到世界每個角落。",
        expandedContent: "我哋嘅全球分銷網絡確保'德國製造'產品以無可比擬嘅效率送達。憑藉策略性佈局嘅樞紐同優化物流，我哋將精密工程產品送至超過100個國家，保持速度、可靠性同可持續性。",
        additionalContent: "我哋利用人工智能驅動嘅供應鏈優化同環保運輸方案，喺歐洲、亞洲同美洲運營分銷中心。與頂尖物流供應商嘅合作保證準時送貨，而碳中和運輸選項符合全球可持續性目標。"
      },
      {
        icon: Lightbulb,
        title: "創新產品設計",
        description: "先鋒設計，憑藉尖端科技驅動。",
        expandedContent: "我哋嘅創新產品設計以'德國製造'嘅創意重新定義行業標準。透過整合人工智能、物聯網同智能材料，我哋創造未來主義產品，融合美學同功能，推動全球市場嘅科技進步。",
        additionalContent: "我哋嘅研發團隊同頂尖工程師合作，開發智能工具、節能裝置同可定制設計，利用3D打印同模擬軟件。我們擁有多項突破性創新嘅專利，成為科技驅動產品開發嘅領導者。"
      }
    ],
    buttonContactUs: "聯繫我們",
    buttonShowMore: "顯示更多",
    buttonShowLess: "顯示較少"
  },
  sg: {
    features: [
      {
        icon: ShieldCheck,
        title: "精密工程",
        description: "无与伦比的工艺，定义全球质量标准。",
        expandedContent: "我们的精密工程展现'德国制造'的极致卓越。每件产品以极致的细节关注打造，运用先进制造技术和严格的质量控制。我们订立耐用性、性能和可靠性的全球标准，深受世界信赖。",
        additionalContent: "我们的尖端设施采用CNC加工、机器人组装和实时质量监控。通过ISO 9001和DIN认证，我们为汽车、航空和工业制造等行业提供定制工程解决方案，确保无与伦比的精度。"
      },
      {
        icon: Truck,
        title: "全球分销网络",
        description: "无缝送货到世界每个角落。",
        expandedContent: "我们的全球分销网络确保'德国制造'产品以无与伦比的效率送达。凭借策略性布局的枢纽和优化物流，我们将精密工程产品送至超过100个国家，保持速度、可靠性和可持续性。",
        additionalContent: "我们利用人工智能驱动的供应链优化和环保运输方案，在欧洲、亚洲和美洲运营分销中心。与顶尖物流供应商的合作保证准时送货，而碳中和运输选项符合全球可持续性目标。"
      },
      {
        icon: Lightbulb,
        title: "创新产品设计",
        description: "先锋设计，凭借尖端科技驱动。",
        expandedContent: "我们的创新产品设计以'德国制造'的创意重新定义行业标准。通过整合人工智能、物联网和智能材料，我们创造未来主义产品，融合美学和功能，推动全球市场的科技进步。",
        additionalContent: "我们的研发团队与顶尖工程师合作，开发智能工具、节能装置和可定制设计，利用3D打印和模拟软件。我们拥有多项突破性创新的专利，成为科技驱动产品开发的领导者。"
      }
    ],
    buttonContactUs: "联系我们",
    buttonShowMore: "显示更多",
    buttonShowLess: "显示较少"
  },
  za: {
    features: [
      {
        icon: ShieldCheck,
        title: "PRESISIE-INGENIEURSWESEN",
        description: "Onovertroffen vakmanskap wat wêreldgehalte-standaarde bepaal.",
        expandedContent: "Ons Presisie-Ingenieurswese beliggaam die toppunt van 'Made in Germany' uitnemendheid. Elke produk word met uiterste sorg vervaardig, met geavanceerde vervaardigingstegnieke en streng gehaltekontroles. Ons stel die standaard vir duursaamheid, prestasie en betroubaarheid, wêreldwyd vertrou.",
        additionalContent: "Ons ultramoderne fasiliteite maak gebruik van CNC-bewerking, robotgestuurde samestelling en real-time gehaltebewaking. Gekeur volgens ISO 9001 en DIN-standaarde, bied ons op maat gemaakte ingenieursoplossings vir bedrywe soos motor, lugvaart en industriële vervaardiging, met ongeëwenaarde presisie."
      },
      {
        icon: Truck,
        title: "WÊRELDWYDE DISTRIBUSIENETWERK",
        description: "Naadlose aflewering na elke uithoek van die wêreld.",
        expandedContent: "Ons Wêreldwye Distribusienetwerk verseker dat 'Made in Germany' produkte u bereik met ongeëwenaarde doeltreffendheid. Met strategies geplaaste hubs en geoptimeerde logistiek, lewer ons presisie-geproduseerde goedere aan meer as 100 lande, met behoud van snelheid, betroubaarheid en volhoubaarheid by elke versending.",
        additionalContent: "Met AI-gedrewe voorsieningsketting-optimalisering en ekovriendelike vervoeroplossings, bestuur ons distribusiesentrums in Europa, Asië en Amerika. Ons vennootskappe met leidende logistieke verskaffers waarborg tydige aflewering, terwyl ons koolstofneutrale versendingsopsies aansluit by globale volhoubaarheidsdoelwitte."
      },
      {
        icon: Lightbulb,
        title: "INNOVERENDE PRODUKONTWERP",
        description: "Pioniersontwerpe aangedryf deur geavanceerde tegnologie.",
        expandedContent: "Ons Innovatiewe Produkontwerp herdefinieer industriestandaarde met 'Made in Germany' vernuf. Ons integreer KI, IoT en slim materiale om futuristiese produkte te skep wat estetika met funksionaliteit meng, en tegnologiese vooruitgang op wêreldmarkte dryf.",
        additionalContent: "Ons N&O-span werk saam met top-ingenieurs om slim gereedskap, energie-ekonomiese toestelle en aanpasbare ontwerpe te ontwikkel met 3D-druk en simulasiesoftware. Ons besit verskeie patente vir baanbrekende innovasies, wat ons posisioneer as leiers in tegnologiegedrewe produkontwikkeling."
      }
    ],
    buttonContactUs: "Kontak Ons",
    buttonShowMore: "Wys Meer",
    buttonShowLess: "Wys Minder"
  },
  kr: {
    features: [
      {
        icon: ShieldCheck,
        title: "정밀 공학",
        description: "비할 데 없는 장인 정신이 글로벌 품질 기준을 정의합니다.",
        expandedContent: "우리의 정밀 공학은 '메이드 인 저먼니'의 극한 우수성을 보여줍니다. 각 제품은 세심한 디테일에 주의를 기울여 제작되며, 고급 제조 기술과 엄격한 품질 관리를 활용합니다. 우리는 내구성, 성능, 신뢰성의 세계적 기준을 설정하며, 전 세계적으로 신뢰받습니다.",
        additionalContent: "우리의 최첨단 시설은 CNC 가공, 로봇 조립, 실시간 품질 모니터링을 사용합니다. ISO 9001 및 DIN 인증을 받은 우리는 자동차, 항공우주, 산업 제조와 같은 산업에 맞춤형 엔지니어링 솔루션을 제공하며, 비교할 수 없는 정밀도를 보장합니다."
      },
      {
        icon: Truck,
        title: "글로벌 유통 네트워크",
        description: "세계 곳곳으로의 원활한 배송.",
        expandedContent: "우리 글로벌 유통 네트워크는 '메이드 인 저먼니' 제품을 탁월한 효율성으로 전달합니다. 전략적으로 배치된 허브와 최적화된 물류를 통해 100개 이상의 국가에 정밀 공학 제품을 배송하며, 속도, 신뢰성, 지속 가능성을 유지합니다.",
        additionalContent: "AI 기반 공급망 최적화와 친환경 운송 솔루션을 활용해 유럽, 아시아, 아메리카에서 유통 센터를 운영합니다. 주요 물류 제공업체와의 파트너십은 제때 배송을 보장하며, 탄소 중립 배송 옵션은 글로벌 지속 가능성 목표에 부합합니다."
      },
      {
        icon: Lightbulb,
        title: "혁신적인 제품 디자인",
        description: "첨단 기술로 구동되는 선구적인 디자인.",
        expandedContent: "우리의 혁신적인 제품 디자인은 '메이드 인 저먼니'의 창의성으로 산업 표준을 재정의합니다. AI, IoT, 스마트 재료를 통합하여 미학과 기능성을 결합한 미래형 제품을 창조하며, 글로벌 시장에서 기술 발전을 이끕니다.",
        additionalContent: "우리 R&D 팀은 최고의 엔지니어들과 협력하여 스마트 도구, 에너지 효율적인 장치, 맞춤형 디자인을 3D 프린팅과 시뮬레이션 소프트웨어로 개발합니다. 우리는 혁신적인 특허를 다수 보유하며, 기술 기반 제품 개발의 리더로 자리 잡았습니다."
      }
    ],
    buttonContactUs: "문의하기",
    buttonShowMore: "더 보기",
    buttonShowLess: "줄이기"
  },
  jp: {
    features: [
      {
        icon: ShieldCheck,
        title: "精密工学",
        description: "比類なき職人技が世界的な品質基準を定義。",
        expandedContent: "私たちの精密工学は'メイド・イン・ジャーマニー'の卓越性の頂点を体現します。各製品は細部にわたる注意を払って製作され、先進的な製造技術と厳格な品質管理を活用します。耐久性、パフォーマンス、信頼性の世界基準を設定し、世界中で信頼されています。",
        additionalContent: "最先端の施設ではCNC加工、ロボット組立、リアルタイム品質監視を活用します。ISO 9001およびDIN規格に認定され、自動車、航空宇宙、産業製造などの業界向けにカスタマイズされたエンジニアリングソリューションを提供し、比類なき精度を保証します。"
      },
      {
        icon: Truck,
        title: "グローバル配送ネットワーク",
        description: "世界中の隅々までシームレスな配送。",
        expandedContent: "私たちのグローバル配送ネットワークは'メイド・イン・ジャーマニー'製品を比類なき効率で届けます。戦略的に配置されたハブと最適化された物流により、100カ国以上に精密工学製品を配送し、速度、信頼性、持続可能性を維持します。",
        additionalContent: "AI駆動のサプライチェーン最適化と環境に優しい輸送ソリューションを活用し、ヨーロッパ、アジア、アメリカで配送センターを運営します。主要物流プロバイダーとのパートナーシップが迅速な配送を保証し、炭素中立配送オプションがグローバルな持続可能性目標に合致します。"
      },
      {
        icon: Lightbulb,
        title: "革新的な製品デザイン",
        description: "最先端技術で駆動される先駆的なデザイン。",
        expandedContent: "私たちの革新的な製品デザインは'メイド・イン・ジャーマニー'の創意で産業基準を再定義します。AI、IoT、スマート素材を統合し、美学と機能を融合した未来的な製品を創造し、グローバル市場で技術進歩を推進します。",
        additionalContent: "R&Dチームはトップエンジニアと協力し、スマートツール、エネルギー効率の高いデバイス、カスタマイズ可能なデザインを3Dプリントとシミュレーションソフトで開発します。革新的な特許を複数保有し、技術駆動型製品開発のリーダーとしての地位を確立しています。"
      }
    ],
    buttonContactUs: "お問い合わせ",
    buttonShowMore: "もっと見る",
    buttonShowLess: "少なく見る"
  }
};

// FeatureCard Component
const FeatureCard: React.FC<FeatureCardProps> = ({ feature, fullWidth, language }) => {
  const Icon = feature.icon;
  const [showMore, setShowMore] = useState<boolean>(false);
  const translations = TRANSLATIONS[language] || TRANSLATIONS['en'];
  const navigate = useNavigate();

  const handleContactClick = () => {
    navigate('/contactform');
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={` mt-10 feature-card relative bg-[#0B111F] rounded-2xl p-6 md:p-8 hover:shadow-2xl transition-all duration-300 
                 border border-gray-700 backdrop-blur-lg overflow-hidden group flex flex-col justify-center items-center text-center ${fullWidth ? 'md:col-span-2' : ''}`}
    >
      <div className="absolute inset-0 bg-[#0B111F] opacity-80" />
      <div className="relative flex flex-col space-y-4 md:space-y-6 items-center w-full">
        <div className="icon-wrapper bg-[#FFFFFF] p-4 rounded-lg relative">
          <div className="absolute inset-0 bg-[#FFFFFF] blur-md opacity-50" />
          <Icon className="w-8 h-8 text-[#2A3139] relative z-10" />
        </div>
        <h3 className="text-white text-lg md:text-xl font-bold leading-tight tracking-wide px-2">{feature.title}</h3>
        <p className="text-gray-300 leading-relaxed px-2">{feature.description}</p>
        <p className="text-gray-400 leading-relaxed px-2">{feature.expandedContent}</p>
        
        {showMore && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="text-gray-400 leading-relaxed px-2"
          >
            {feature.additionalContent}
          </motion.p>
        )}
        
        <div className="flex flex-col md:flex-row gap-3 w-full px-4 justify-center items-center">
          <button
            className="px-6 py-2 w-50 md:w-50 rounded-[5px] bg-[#FFFFFF] text-[#0B111F] font-medium transition-all duration-200 hover:bg-[#0B111F] hover:text-white hover:shadow-[0px_0px_15px_5px_#FFFFFF]"
            onClick={handleContactClick}
          >
            {translations.buttonContactUs}
          </button>
          
          <button
            className="px-2 py-2 w-40 md:w-40 rounded-[5px] bg-transparent text-white font-medium border border-[#FFFFFF] transition-all duration-200 hover:bg-[#0B111F] hover:text-white hover:shadow-[0px_0px_15px_5px_#0B111F]"
            onClick={() => setShowMore(!showMore)}
          >
            {showMore ? translations.buttonShowLess : translations.buttonShowMore}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// FeatureSections Component
const FeatureSections = ({ language = 'en' }) => {
  const translations = TRANSLATIONS[language] || TRANSLATIONS['en'];

  return (
    <section className="w-full overflow-hidden px-4 md:px-8">
      <div className="max-w-7xl mx-auto mb-10">
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 gap-1" // Updated to a fixed 10px gap
        >
          <FeatureCard
            feature={translations.features[0]}
            fullWidth
            language={language}
          />
          {translations.features.slice(1).map((feature, index) => (
            <FeatureCard
              key={index + 1}
              feature={feature}
              language={language}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FeatureSections;